import { RootState } from '@/store/store';
import { createSelector } from '@reduxjs/toolkit';
import type { WidgetDomainsListState } from '@/store/types/widgetDomainsListTypes';

/** Запасной лимит, если бэкенд не прислал число (обратная совместимость). */
export const WIDGET_MAX_DOMAINS_FALLBACK = 10;

const selectWidgetDomainsListState = (state: RootState): WidgetDomainsListState =>
	state.widgetDomainsList as WidgetDomainsListState;

export const selectWidgetDomainsList = (state: RootState): string[] => selectWidgetDomainsListState(state).data;

export const selectAllowDomains = createSelector(
	[selectWidgetDomainsList],
	(domainsList) => domainsList,
);

export const selectWidgetDomainsMaxRaw = (state: RootState): number | null =>
	selectWidgetDomainsListState(state).maxAllowedDomains;

/** Эффективный лимит доменов для UI и сравнений (всегда ≥ 1). */
export const selectWidgetDomainsMaxAllowed = createSelector(
	[selectWidgetDomainsMaxRaw],
	(max) => (max != null && max > 0 ? max : WIDGET_MAX_DOMAINS_FALLBACK),
);

export const selectErrorDomainsList = createSelector(
	[(state: RootState) => selectWidgetDomainsListState(state).error],
	(error) => error,
);

export const selectIsFetchingDomainsList = createSelector(
	[(state: RootState) => selectWidgetDomainsListState(state).isFetching],
	(isFetching) => isFetching,
);

export const selectIsNetworkError = createSelector(
	[(state: RootState) => selectWidgetDomainsListState(state).isNetworkError],
	(isNetworkError) => isNetworkError,
);

/** Сколько доменов уже сохранено у пользователя. */
export const selectWidgetDomainsCount = createSelector(
	[selectAllowDomains],
	(domains) => domains.length,
);

/**
 * Готовый текст-счётчик «N / M» — используется в шапке аккордеона
 * «Разрешённые домены» (см. `MyWidgets`). Собираем в одном месте, чтобы
 * не повторять формат «N / M» в компонентах. См. `business-logic.mdc`.
 */
export const selectWidgetDomainsCountLabel = createSelector(
	[selectWidgetDomainsCount, selectWidgetDomainsMaxAllowed],
	(count, max) => `${count} / ${max}`,
);

/**
 * Можно ли добавить ещё домен. Сводим два поля стора (текущее
 * количество и эффективный лимит) в один derived-флаг, чтобы
 * компонент не считал условие из нескольких `useSelector`.
 */
export const selectCanAddMoreDomains = createSelector(
	[selectWidgetDomainsCount, selectWidgetDomainsMaxAllowed],
	(count, max) => count < max,
);
